'use client';

import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { WizardData } from './ContentGeneratorWizard';

interface Props {
  data: WizardData;
  onUpdate: (partial: Partial<WizardData>) => void;
  onBack: () => void;
  onNext: () => void;
}

const platformOptions = [
  { id: 'plat-opt-li', value: 'LI', label: 'LinkedIn', desc: 'Experten-Artikel, Thought Leadership, Newsletter', color: '#0A66C2', tone: 'Professionell' },
  { id: 'plat-opt-ig', value: 'IG', label: 'Instagram', desc: 'Reels-Scripts, 10-Slide Carousels, Story-Sequenzen', color: '#C13584', tone: 'Emotional' },
  { id: 'plat-opt-tt', value: 'TT', label: 'TikTok', desc: 'High-Energy Video Scripts, POV/Rebel Style', color: '#FF0050', tone: 'Provokativ' },
  { id: 'plat-opt-fb', value: 'FB', label: 'Facebook', desc: 'Community-Posts, Satirische Business-Fails', color: '#1877F2', tone: 'Community' },
  { id: 'plat-opt-x', value: 'X', label: 'X (Twitter)', desc: 'Virale Threads, Duct Tape Statements', color: '#ccc', tone: 'Viral' },
  { id: 'plat-opt-blog', value: 'BLOG', label: 'Blog / SEO', desc: 'Langform-Artikel, 2.000+ Wörter', color: '#FF5F1F', tone: 'Informativ' },
];

const toneOptions = [
  { id: 'tone-opt-prov', value: 'Provokativ', label: 'Provokativ' },
  { id: 'tone-opt-auth', value: 'Authentisch', label: 'Authentisch' },
  { id: 'tone-opt-human', value: 'Human-Centric', label: 'Human-Centric' },
  { id: 'tone-opt-punk', value: 'Business-Punk', label: 'Business-Punk' },
];

export default function WizardStep2Configure({ data, onUpdate, onBack, onNext }: Props) {
  const togglePlatform = (value: string) => {
    const current = data.platforms;
    if (current.includes(value)) {
      if (current.length > 1) onUpdate({ platforms: current.filter((p) => p !== value) });
    } else {
      onUpdate({ platforms: [...current, value] });
    }
  };

  const canProceed = data.platforms.length > 0;

  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
      {/* Platforms */}
      <div className="punk-card p-5">
        <p className="section-label mb-3">Zielplattformen</p>
        <h3 className="text-sm font-600 text-white mb-4">Welche Kanäle sollen bespielt werden?</h3>
        <div className="space-y-2">
          {platformOptions.map((plat) => {
            const isSelected = data.platforms.includes(plat.value);
            return (
              <button
                key={plat.id}
                onClick={() => togglePlatform(plat.value)}
                className={`w-full text-left p-3.5 rounded border transition-all duration-150 flex items-center gap-3 ${
                  isSelected ? 'border-accent/50 bg-accent/5' : 'border-border bg-surface hover:border-accent/30'
                }`}
              >
                <div
                  className={`w-2 h-2 rounded-full flex-shrink-0 transition-all ${isSelected ? 'opacity-100' : 'opacity-40'}`}
                  style={{ background: plat.color }}
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-600 ${isSelected ? 'text-white' : 'text-muted'}`}>{plat.label}</span>
                    <span className="text-xs mono text-muted-2 border border-border px-1.5 py-0.5 rounded">{plat.tone}</span>
                  </div>
                  <span className="text-xs text-muted">{plat.desc}</span>
                </div>
                <div className={`w-4 h-4 rounded border flex items-center justify-center transition-all ${
                  isSelected ? 'bg-accent border-accent' : 'border-border'
                }`}>
                  {isSelected && <span className="text-black text-xs font-700">✓</span>}
                </div>
              </button>
            );
          })}
        </div>
        <p className="text-xs text-muted mt-3 mono">{data.platforms.length} Plattform{data.platforms.length !== 1 ? 'en' : ''} ausgewählt</p>

        {/* Tone auto-adjust note */}
        <div className="mt-3 p-3 bg-accent/5 border border-accent/20 rounded">
          <p className="text-xs text-muted">
            <span className="text-accent font-600">Auto-Ton:</span> Gemini passt den Schreibstil automatisch pro Plattform an — professionell für LinkedIn, provokativ für TikTok.
          </p>
        </div>
      </div>

      {/* Tone + Count */}
      <div className="space-y-4">
        <div className="punk-card p-5">
          <p className="section-label mb-3">Basis-Tonalität (Override)</p>
          <h3 className="text-sm font-600 text-white mb-4">Brand Vault Ton überschreiben?</h3>
          <div className="grid grid-cols-2 gap-2">
            {toneOptions.map((tone) => (
              <button
                key={tone.id}
                onClick={() => onUpdate({ tone: tone.value })}
                className={`p-3 rounded border text-xs font-600 transition-all ${
                  data.tone === tone.value
                    ? 'border-accent bg-accent/10 text-accent' :'border-border bg-surface text-muted hover:border-accent/40 hover:text-white'
                }`}
              >
                {tone.label}
              </button>
            ))}
          </div>
          <p className="text-xs text-muted mt-3">
            Brand Vault Ton: <span className="text-accent mono">Business-Punk</span>
          </p>
        </div>

        <div className="punk-card p-5">
          <p className="section-label mb-3">Beitrags-Anzahl</p>
          <h3 className="text-sm font-600 text-white mb-4">Wie viele Beiträge sollen generiert werden?</h3>
          <div className="flex items-center gap-4">
            <input
              type="range"
              min={7}
              max={28}
              step={7}
              value={data.postCount}
              onChange={(e) => onUpdate({ postCount: Number(e.target.value) })}
              className="flex-1 accent-[#FF5F1F]"
            />
            <span className="mono text-2xl font-700 text-accent w-12 text-right">{data.postCount}</span>
          </div>
          <div className="flex justify-between text-xs text-muted mono mt-1">
            <span>7 Tage</span>
            <span>14 Tage</span>
            <span>21 Tage</span>
            <span>28 Tage</span>
          </div>
        </div>

        {/* Summary */}
        <div className="punk-card-accent p-4 neon-glow">
          <p className="section-label mb-2">Zusammenfassung</p>
          <div className="space-y-1.5 text-xs">
            <div className="flex justify-between">
              <span className="text-muted">Plattformen:</span>
              <span className="mono text-white">{data.platforms.join(', ')}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted">Basis-Ton:</span>
              <span className="mono text-accent">{data.tone}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted">Beiträge:</span>
              <span className="mono text-white">{data.postCount}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted">KI-Modell:</span>
              <span className="mono text-accent">Gemini 2.5 Flash</span>
            </div>
          </div>
        </div>

        <div className="flex justify-between">
          <button onClick={onBack} className="btn-ghost">
            <ChevronLeft size={14} />
            Zurück
          </button>
          <button onClick={onNext} disabled={!canProceed} className="btn-primary">
            Generieren starten
            <ChevronRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}